import { useMemo } from "react"
import { DateTime } from "luxon"
import { EstatusGrupoTarifa, EstatusTarifa, ModalidadGrupoTarifa, Tarifa, TiposAlojamientos } from "src/gql/schema"
import { useTarifasValidation } from "src/shared/hooks/useTarifasValidation"
import { months } from "src/utils/date"

const getFecha = (fecha: string) => {
    const date = DateTime.fromISO(fecha.substring(0, 10))
    const day = date.day
    const month = months[date.month - 1] || ""

    return `${day} de ${month.slice(0, 3)}`
}

function getHotelOptions(tarifas: Tarifa[]) {
    return tarifas.map((t) => {
        const grupo = t?.grupo_tarifa
        const modalidad = grupo?.modalidad || ""
        const fechas = `${getFecha(t.fecha_inicio)}${t.fecha_final ? ` al ${getFecha(t.fecha_final)}` : ""}`
        return {
            label: modalidad === "base" ? "Tarifa base" : `${(grupo?.nombre  ||  t.nombre || "")} (${fechas})`,
            value: t.tarifa_id,
            withCheckbox: true,
            modalidad,
            fechas
        }
    })
}

function getMotelOptions(tarifas: Tarifa[]) {
    return tarifas.map((t) => {
        const grupo = t?.grupo_tarifa
        const modalidad = t?.grupo_tarifa?.modalidad || ""
        return {
            label: `${modalidad === "base" ? "Tarifa base" : (grupo?.nombre  ||  t.nombre || "")} | ${grupo?.duracion || 1} horas`,
            value: t.tarifa_id,
            modalidad
        }
    })
}

export function useOpcionesTarifas({ tarifas = [], fechas = [], showAll = false }: { fechas: Date[], tarifas: Tarifa[], showAll?: boolean }) {
    const { validationByDate, validationByDateRange } = useTarifasValidation()
    const tarifasActivas = useMemo(() => {
        return !showAll ? tarifas.filter(t =>
            !t.eliminado &&
            t.estatus === EstatusTarifa.Activa &&
            (!t.grupo_tarifa || (!t.grupo_tarifa.eliminado && t.grupo_tarifa.estatus === EstatusGrupoTarifa.Activa))
        ) : tarifas
    },
    [tarifas, showAll])

    const opciones_tarifas_hotel = useMemo(() => {
        const tarifas_base: Tarifa[] = []
        const tarifas_temp: Tarifa[] = []

        tarifasActivas.forEach((t) => {
            const modalidad = t?.grupo_tarifa?.modalidad || ""

            if(t.tipo_alojamiento === TiposAlojamientos.Hotel) {
                if (modalidad === ModalidadGrupoTarifa.Base) {
                    tarifas_base.push({ ...t, fecha_inicio: fechas?.[0]?.toISOString(), fecha_final: fechas?.[1]?.toISOString() })
                }

                if (modalidad === ModalidadGrupoTarifa.Temporada && validationByDateRange(t, fechas?.[0], fechas?.[1] || fechas?.[0])) {
                    tarifas_temp.push(t)
                }
            }
            
        })

        return [...tarifas_base, ...tarifas_temp]
    }, [tarifasActivas, fechas])


    const opciones_tarifas_adicionales = useMemo(() => {
        return tarifasActivas.filter((t) => t.tipo_alojamiento === TiposAlojamientos.Hotel && t.grupo_tarifa?.modalidad === ModalidadGrupoTarifa.Adicional && validationByDateRange(t, fechas?.[0], fechas?.[1] || fechas?.[0]))
    }, [tarifasActivas, fechas])

    const opciones_tarifas_motel = useMemo(() => {
        const tarifas_base: Tarifa[] = []
        const tarifas_temp: Tarifa[] = []

        tarifasActivas.forEach((t) => {
            const modalidad = t?.grupo_tarifa?.modalidad || ""
            if ((t.tipo_alojamiento === TiposAlojamientos.Motel && validationByDate(t, fechas?.[0])) || (t.tipo_alojamiento === TiposAlojamientos.Motel && modalidad === ModalidadGrupoTarifa.Base)) {
                if (modalidad === ModalidadGrupoTarifa.Base) {
                    tarifas_base.push({ ...t, fecha_inicio: fechas?.[0]?.toISOString(), fecha_final: fechas?.[1]?.toISOString() })
                } else {
                    tarifas_temp.push(t)
                }
            }
        })

        return [...tarifas_base, ...tarifas_temp]
    }, [tarifasActivas, fechas])

    return {
        opciones_tarifas_motel: getMotelOptions(opciones_tarifas_motel),
        opciones_tarifas_hotel: getHotelOptions(opciones_tarifas_hotel),
        opciones_tarifas_adicionales: getHotelOptions(opciones_tarifas_adicionales),
    }
}